import { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Gavel, MapPin, Clock, TrendingUp, BedDouble, Maximize, CheckCircle2, Trophy, Lock, LogIn, UserPlus, Eye, ShieldCheck, Building2, IndianRupee, KeyRound, CalendarClock, Phone, Heart, Share2 } from "lucide-react";
import { api, formatINR, fileUrl, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import Seo from "../components/Seo";

function useCountdown(targetIso) {
    const [now, setNow] = useState(Date.now());
    useEffect(() => {
        const t = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(t);
    }, []);
    const diff = new Date(targetIso).getTime() - now;
    if (diff <= 0) return { ended: true, d: 0, h: 0, m: 0, s: 0 };
    return {
        ended: false,
        d: Math.floor(diff / 86400000),
        h: Math.floor((diff % 86400000) / 3600000),
        m: Math.floor((diff % 3600000) / 60000),
        s: Math.floor((diff % 60000) / 1000),
    };
}

export default function AuctionDetail() {
    const { id } = useParams();
    const nav = useNavigate();
    const { user } = useAuth();
    const [a, setA] = useState(null);
    const [bids, setBids] = useState([]);
    const [active, setActive] = useState(0);
    const [amount, setAmount] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const refresh = useCallback(async () => {
        try {
            const { data } = await api.get(`/auctions/${id}`);
            setA(data);
            const { data: b } = await api.get(`/auctions/${id}/bids`);
            setBids(b || []);
        } catch {
            setA(false);
        }
    }, [id]);

    useEffect(() => { refresh(); }, [refresh]);
    useEffect(() => {
        // Poll every 5s for live updates (only when logged in — anonymous viewers see the wall)
        if (!user) return;
        const t = setInterval(refresh, 5000);
        return () => clearInterval(t);
    }, [refresh, user]);

    const cd = useCountdown(a?.end_time || new Date().toISOString());

    if (a === null) return <div className="min-h-screen flex items-center justify-center text-gold-500">Loading…</div>;
    if (a === false) return (
        <div className="min-h-screen flex items-center justify-center text-center">
            <div>
                <h2 className="text-2xl font-heading font-bold text-white">Auction not found</h2>
                <Link to="/auctions" className="text-gold-500 mt-4 inline-block">← Back to auctions</Link>
            </div>
        </div>
    );

    // Login wall: anonymous visitors see a premium gate instead of auction details.
    if (!user) return <AuctionLoginWall auction={a} />;

    const images = a.images?.length ? a.images : ["https://images.unsplash.com/photo-1638454795595-0a0abf68614d?crop=entropy&cs=srgb&fm=jpg&q=85"];
    const minNext = a.current_price + (a.bid_increment || 50000);
    const isLive = a.status === "live";

    const onBid = async (e) => {
        e.preventDefault();
        if (!user) {
            toast.error("Please login to place a bid");
            nav("/login");
            return;
        }
        const amt = Number(amount);
        if (!amt || amt < minNext) {
            toast.error(`Bid must be at least ₹${minNext.toLocaleString("en-IN")}`);
            return;
        }
        setSubmitting(true);
        try {
            await api.post(`/auctions/${id}/bid`, { amount: amt });
            toast.success("Bid placed!");
            setAmount("");
            await refresh();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setSubmitting(false);
        }
    };

    const StatusBadge = () => {
        if (a.status === "live") return <span className="px-3 py-1 rounded-full bg-red-500 text-white text-xs font-bold uppercase tracking-widest flex items-center gap-1.5 animate-pulse w-fit"><span className="w-2 h-2 rounded-full bg-white" /> Live</span>;
        if (a.status === "upcoming") return <span className="px-3 py-1 rounded-full bg-gold-500 text-white text-xs font-bold uppercase tracking-widest w-fit">Upcoming</span>;
        return <span className="px-3 py-1 rounded-full bg-navy-800 text-white text-xs font-bold uppercase tracking-widest w-fit">Ended</span>;
    };

    return (
        <div className="min-h-screen">
            <div className="max-w-7xl mx-auto px-6 lg:px-8 py-8">
                <Link to="/auctions" className="text-sm text-white/60 hover:text-gold-500 inline-flex items-center gap-2 mb-6">
                    <ArrowLeft className="w-4 h-4" /> Back to auctions
                </Link>

                <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-6">
                    <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                            <StatusBadge />
                            {a.auction_type && <span className="text-[10px] uppercase tracking-[0.18em] px-2 py-1 rounded-full bg-gold-500/10 border border-gold-500/30 text-gold-300 font-bold">{a.auction_type}</span>}
                        </div>
                        {a.borrower_name ? (
                            <>
                                <div className="text-xs uppercase tracking-[0.18em] text-white/55 font-bold mb-1">Borrower</div>
                                <h1 className="font-heading text-3xl lg:text-5xl font-bold text-white" data-testid="auction-borrower-heading">{a.borrower_name}</h1>
                                <div className="text-white/60 mt-1 text-base">{a.title}</div>
                            </>
                        ) : (
                            <h1 className="font-heading text-3xl lg:text-5xl font-bold text-white">{a.title}</h1>
                        )}
                        <div className="text-white/65 mt-2 flex items-center gap-2"><MapPin className="w-4 h-4 text-gold-500" /> {a.locality && `${a.locality}, `}{a.city}, {a.state}</div>
                    </div>
                    <SaveShareActions auction={a} />
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-3 mb-10">
                    <div className="lg:col-span-8 rounded-2xl overflow-hidden bg-navy-800 aspect-[16/10]">
                        <img src={fileUrl(images[active])} alt={a.title} className="w-full h-full object-cover" />
                    </div>
                    <div className="lg:col-span-4 grid grid-cols-2 gap-3">
                        {images.slice(0, 4).map((img, i) => (
                            <button key={i} onClick={() => setActive(i)} className={`rounded-xl overflow-hidden bg-navy-800 aspect-square border-2 ${active === i ? "border-gold-500" : "border-transparent"}`}>
                                <img src={fileUrl(img)} alt="" className="w-full h-full object-cover" />
                            </button>
                        ))}
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                    <div className="lg:col-span-8 space-y-6">
                        <div className="rounded-2xl bg-navy-800 border border-white/5 p-7 grid grid-cols-2 md:grid-cols-4 gap-5">
                            <Spec icon={Gavel} label="Base Price" value={formatINR(a.base_price)} />
                            <Spec icon={TrendingUp} label="Bid Increment" value={formatINR(a.bid_increment)} />
                            {a.bhk != null && <Spec icon={BedDouble} label="Bedrooms" value={`${a.bhk} BHK`} />}
                            {a.area_sqft != null && <Spec icon={Maximize} label="Area" value={`${a.area_sqft} sqft`} />}
                        </div>

                        {/* Bank Auction Information — SARFAESI / DRT / Court order details */}
                        {(a.bank_name || a.borrower_name || a.emd_amount || a.possession_type || a.inspection_date || a.emd_submission_deadline) && (
                            <div className="rounded-2xl bg-gradient-to-br from-navy-800 via-navy-800 to-navy-700/50 border border-gold-500/30 p-7" data-testid="bank-auction-info">
                                <h3 className="label-overline mb-5 flex items-center gap-2"><Building2 className="w-4 h-4 text-gold-500" /> Bank Auction Information</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {a.borrower_name && <InfoRow icon={ShieldCheck} label="Borrower Name" value={a.borrower_name} />}
                                    {a.bank_name && <InfoRow icon={Building2} label="Bank / Lender" value={a.bank_name} />}
                                    {a.branch && <InfoRow icon={MapPin} label="Branch" value={a.branch} />}
                                    {a.auction_type && <InfoRow icon={Gavel} label="Auction Type" value={a.auction_type} />}
                                    {a.possession_type && <InfoRow icon={KeyRound} label="Possession" value={a.possession_type === "physical" ? "Physical Possession" : "Symbolic Possession"} />}
                                    {a.emd_amount != null && <InfoRow icon={IndianRupee} label="EMD Amount" value={formatINR(a.emd_amount)} />}
                                    {a.emd_submission_deadline && <InfoRow icon={CalendarClock} label="EMD Submission Deadline" value={formatDateTime(a.emd_submission_deadline)} />}
                                    {a.inspection_date && <InfoRow icon={Eye} label="Inspection Date" value={formatDateTime(a.inspection_date)} />}
                                    {a.contact_person && <InfoRow icon={Phone} label="Contact Person" value={a.contact_person + (a.contact_phone ? ` · ${a.contact_phone}` : "")} />}
                                </div>
                                <p className="text-[11px] text-white/45 mt-5 leading-relaxed">
                                    Note: All bank-auction details above are sourced from official notices. Verify with the listed bank/contact before depositing EMD or participating.
                                </p>
                            </div>
                        )}

                        <div className="rounded-2xl bg-navy-800 border border-white/5 p-7">
                            <h3 className="label-overline mb-4">About this auction</h3>
                            <p className="text-white/75 leading-relaxed whitespace-pre-line">{a.description}</p>
                            {a.address && <p className="text-white/60 text-sm mt-3"><MapPin className="w-4 h-4 inline mr-1 text-gold-500" />{a.address}</p>}
                        </div>

                        {a.amenities?.length > 0 && (
                            <div className="rounded-2xl bg-navy-800 border border-white/5 p-7">
                                <h3 className="label-overline mb-4">Features</h3>
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                    {a.amenities.map((x) => (
                                        <div key={x} className="flex items-center gap-2 text-white/80 text-sm">
                                            <CheckCircle2 className="w-4 h-4 text-gold-500" /> {x}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="rounded-2xl bg-navy-800 border border-white/5 p-7" data-testid="bid-history">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="label-overline">Bid History</h3>
                                <span className="text-xs text-white/55">{bids.length} bids · auto-updating</span>
                            </div>
                            {bids.length === 0 ? (
                                <p className="text-white/55 text-sm py-8 text-center">No bids yet. Be the first to bid!</p>
                            ) : (
                                <div className="space-y-2">
                                    {bids.map((b, i) => (
                                        <div key={b.id} className={`flex items-center justify-between p-3 rounded-lg ${i === 0 ? "bg-gold-500/10 border border-gold-500/30" : "bg-navy-900 border border-white/5"}`}>
                                            <div className="flex items-center gap-3">
                                                {i === 0 && <Trophy className="w-4 h-4 text-gold-500" />}
                                                <div>
                                                    <div className="font-semibold text-white text-sm">{b.user_name}{user?.id === b.user_id && <span className="text-[10px] ml-2 text-gold-500">YOU</span>}</div>
                                                    <div className="text-[11px] text-white/50">{new Date(b.created_at).toLocaleString()}</div>
                                                </div>
                                            </div>
                                            <div className="font-heading font-bold text-gold-500 text-lg">{formatINR(b.amount)}</div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    <aside className="lg:col-span-4 space-y-5">
                        <div className="rounded-2xl bg-navy-800 border border-gold-500/30 p-6 sticky top-32 gold-border-glow">
                            <div className="label-overline">Current Bid</div>
                            <div className="font-heading text-4xl font-bold text-gold-500 mt-2">{formatINR(a.current_price)}</div>
                            {a.top_bidder_name && <div className="text-xs text-white/55 mt-1">by <span className="text-white font-medium">{a.top_bidder_name}</span></div>}

                            <div className="mt-5 p-4 rounded-xl bg-navy-900 border border-white/5" data-testid="countdown">
                                <div className="text-[10px] uppercase tracking-widest text-white/50 mb-2">
                                    {a.status === "upcoming" ? "Auction starts in" : a.status === "live" ? "Auction ends in" : "Auction ended"}
                                </div>
                                <div className="grid grid-cols-4 gap-2 text-center">
                                    {[["Days", cd.d], ["Hrs", cd.h], ["Min", cd.m], ["Sec", cd.s]].map(([l, v]) => (
                                        <div key={l} className="bg-navy-800 rounded-lg p-2">
                                            <div className="font-heading text-2xl font-bold text-gold-500 tabular-nums">{String(v).padStart(2, "0")}</div>
                                            <div className="text-[9px] uppercase tracking-wider text-white/50 mt-0.5">{l}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {isLive && (
                                <form onSubmit={onBid} className="mt-5 space-y-3" data-testid="bid-form">
                                    <div>
                                        <label className="text-xs text-white/60 mb-1 block">Your bid (min ₹{minNext.toLocaleString("en-IN")})</label>
                                        <input
                                            type="number"
                                            value={amount}
                                            onChange={(e) => setAmount(e.target.value)}
                                            min={minNext}
                                            step={a.bid_increment}
                                            placeholder={`${minNext}`}
                                            data-testid="bid-amount-input"
                                            className="w-full bg-navy-900 border border-white/10 rounded-md px-4 py-3 text-base text-white placeholder:text-white/40 font-mono"
                                            required
                                        />
                                    </div>
                                    <div className="flex gap-2">
                                        {[minNext, minNext + a.bid_increment, minNext + a.bid_increment * 4].map((v) => (
                                            <button key={v} type="button" onClick={() => setAmount(String(v))} className="flex-1 text-xs px-2 py-1.5 rounded border border-white/10 text-white/70 hover:border-gold-500/40 hover:text-gold-500">
                                                +{formatINR(v - a.current_price)}
                                            </button>
                                        ))}
                                    </div>
                                    <button
                                        disabled={submitting}
                                        type="submit"
                                        data-testid="place-bid-btn"
                                        className="w-full px-5 py-3.5 rounded-md bg-gold-500 text-white font-bold text-sm hover:bg-gold-600 disabled:opacity-60 flex items-center justify-center gap-2"
                                    >
                                        <Gavel className="w-4 h-4" /> {submitting ? "Placing…" : "Place Bid"}
                                    </button>
                                    {!user && <p className="text-[11px] text-white/55 text-center"><Link to="/login" className="text-gold-500 underline">Login</Link> required to bid</p>}
                                </form>
                            )}
                            {a.status === "upcoming" && (
                                <div className="mt-5 p-4 rounded-xl bg-gold-500/10 border border-gold-500/30 text-sm text-white/80 text-center">
                                    🔔 Bidding will open at scheduled start time.
                                </div>
                            )}
                            {a.status === "ended" && a.top_bidder_name && (
                                <div className="mt-5 p-4 rounded-xl bg-gold-500/10 border border-gold-500/30 text-sm text-white/80 text-center">
                                    <Trophy className="w-5 h-5 inline mr-2 text-gold-500" /> <strong className="text-gold-500">{a.top_bidder_name}</strong> won this auction at {formatINR(a.current_price)}
                                </div>
                            )}
                        </div>
                    </aside>
                </div>
            </div>
        </div>
    );
}

function Spec({ icon: Icon, label, value }) {
    return (
        <div>
            <div className="flex items-center gap-2 text-gold-500 mb-1"><Icon className="w-4 h-4" /><span className="text-[11px] uppercase tracking-wider">{label}</span></div>
            <div className="text-white font-semibold">{value}</div>
        </div>
    );
}

function InfoRow({ icon: Icon, label, value }) {
    return (
        <div className="flex items-start gap-3 p-3 rounded-lg bg-navy-950/40 border border-white/5">
            <div className="w-9 h-9 rounded-md bg-gold-500/15 border border-gold-500/30 flex items-center justify-center shrink-0">
                <Icon className="w-4 h-4 text-gold-500" />
            </div>
            <div className="min-w-0 flex-1">
                <div className="text-[10px] uppercase tracking-[0.15em] text-white/55 font-bold">{label}</div>
                <div className="text-white text-sm font-semibold mt-0.5 break-words">{value}</div>
            </div>
        </div>
    );
}

function formatDateTime(iso) {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        if (isNaN(d.getTime())) return iso;
        return d.toLocaleString("en-IN", {
            day: "2-digit", month: "short", year: "numeric",
            hour: "2-digit", minute: "2-digit", hour12: true,
        });
    } catch (e) {
        return iso;
    }
}

function SaveShareActions({ auction }) {
    const { user } = useAuth();
    const [saved, setSaved] = useState(false);
    const [savingNow, setSavingNow] = useState(false);

    useEffect(() => {
        if (!user || !auction?.id) return;
        api.get("/saved/auctions").then((r) => {
            setSaved((r.data || []).some((x) => x.id === auction.id));
        }).catch(() => {});
    }, [user, auction?.id]);

    const onSave = async () => {
        if (!user) {
            toast.error("Please log in to save auctions");
            return;
        }
        setSavingNow(true);
        try {
            if (saved) {
                await api.delete(`/saved/auctions/${auction.id}`);
                setSaved(false);
                toast.success("Removed from saved");
            } else {
                await api.post(`/saved/auctions/${auction.id}`);
                setSaved(true);
                toast.success("Auction saved");
            }
        } catch (e) {
            toast.error("Unable to update saved list");
        } finally {
            setSavingNow(false);
        }
    };

    const onShare = async () => {
        const shareUrl = `${window.location.origin}/auctions/${auction.id}`;
        const title = auction.borrower_name
            ? `Bank Auction — ${auction.borrower_name}`
            : auction.title || "Bank Auction Property";
        const shareText = `${title} · ${auction.bank_name || ""}${auction.city ? ` · ${auction.city}` : ""}\n${shareUrl}`;
        if (navigator.share) {
            try {
                await navigator.share({ title, text: shareText, url: shareUrl });
                return;
            } catch (e) {
                /* user cancelled — fall through to copy */
            }
        }
        try {
            await navigator.clipboard.writeText(shareUrl);
            toast.success("Link copied to clipboard");
        } catch (e) {
            toast.error("Unable to share");
        }
    };

    return (
        <div className="flex gap-2 self-start md:self-end shrink-0">
            <button
                onClick={onSave}
                disabled={savingNow}
                data-testid="auction-save-btn"
                className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border text-sm font-semibold transition-colors ${
                    saved
                        ? "bg-gold-500/20 border-gold-500 text-gold-300"
                        : "bg-white/5 border-white/15 text-white hover:bg-white/10"
                }`}
            >
                <Heart className={`w-4 h-4 ${saved ? "fill-gold-500 text-gold-500" : ""}`} />
                {saved ? "Saved" : "Save"}
            </button>
            <button
                onClick={onShare}
                data-testid="auction-share-btn"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-white/5 border border-white/15 text-white text-sm font-semibold hover:bg-white/10 transition-colors"
            >
                <Share2 className="w-4 h-4" /> Share
            </button>
        </div>
    );
}


/* -------------------- AUCTION LOGIN WALL -------------------- */
function AuctionLoginWall({ auction: a }) {
    const heroImg = a.images?.[0]
        ? (typeof a.images[0] === "string" && a.images[0].startsWith("http")
            ? a.images[0]
            : fileUrl(a.images[0]))
        : "https://images.unsplash.com/photo-1638454795595-0a0abf68614d?crop=entropy&cs=srgb&fm=jpg&q=85";
    const next = encodeURIComponent(`/auctions/${a.id}`);

    return (
        <div className="min-h-screen bg-navy-950 relative overflow-hidden" data-testid="auction-login-wall">
            <Seo
                title={`${a.title} — Sign in to view auction details`}
                description={`${a.title} in ${a.city}. Sign in to view base price, current bid, photos and place your bid on Global Property Mart.`}
                path={`/auctions/${a.id}`}
            />
            {/* Blurred hero background */}
            <div className="absolute inset-0">
                <img src={heroImg} alt="" className="w-full h-full object-cover scale-110 blur-2xl opacity-30" />
                <div className="absolute inset-0 bg-gradient-to-b from-navy-950/70 via-navy-950/85 to-navy-950" />
            </div>

            <div className="relative max-w-4xl mx-auto px-6 lg:px-8 py-16 lg:py-24">
                <Link
                    to="/auctions"
                    data-testid="auction-loginwall-back-link"
                    className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-gold-500 transition-colors mb-10"
                >
                    <ArrowLeft className="w-4 h-4" /> Back to auctions
                </Link>

                <div className="rounded-3xl bg-navy-900/70 backdrop-blur-2xl border border-gold-500/30 shadow-[0_30px_80px_-20px_rgba(218,165,32,0.25)] overflow-hidden">
                    <div className="grid grid-cols-1 lg:grid-cols-5">
                        {/* Teaser image */}
                        <div className="lg:col-span-2 relative min-h-[220px] lg:min-h-[440px]">
                            <img src={heroImg} alt={a.title} className="absolute inset-0 w-full h-full object-cover" />
                            <div className="absolute inset-0 bg-gradient-to-br from-navy-900/40 via-navy-900/30 to-navy-900/70" />
                            <div className="absolute inset-0 backdrop-blur-[6px]" />
                            <div className="absolute inset-0 flex items-center justify-center">
                                <div className="w-20 h-20 rounded-full bg-gold-500/95 flex items-center justify-center shadow-2xl shadow-gold-500/40 ring-4 ring-white/10">
                                    <Lock className="w-9 h-9 text-navy-900" strokeWidth={2.4} />
                                </div>
                            </div>
                            <div className="absolute top-4 left-4">
                                {a.status === "live" ? (
                                    <span className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-full bg-red-500 text-white font-bold animate-pulse">Live Auction</span>
                                ) : a.status === "upcoming" ? (
                                    <span className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-full bg-gold-500 text-white font-bold">Upcoming</span>
                                ) : (
                                    <span className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-full bg-navy-700 text-white/80 font-bold">Ended</span>
                                )}
                            </div>
                            <div className="absolute bottom-4 left-4 right-4">
                                <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/50 backdrop-blur-sm border border-white/10 text-[10px] font-bold uppercase tracking-[0.18em] text-gold-300">
                                    <Eye className="w-3 h-3" /> Members only
                                </div>
                            </div>
                        </div>

                        {/* CTA panel */}
                        <div className="lg:col-span-3 p-8 lg:p-12">
                            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gold-500/10 border border-gold-500/30 mb-4">
                                <Gavel className="w-3.5 h-3.5 text-gold-400" />
                                <span className="text-[10px] font-bold tracking-[0.18em] uppercase text-gold-300">Verified Auction</span>
                            </div>
                            <h1 className="font-heading text-3xl lg:text-4xl font-bold text-white tracking-tight leading-tight" data-testid="auction-loginwall-title">
                                {a.title}
                            </h1>
                            <div className="flex items-center gap-2 text-sm text-white/70 mt-2">
                                <MapPin className="w-4 h-4 text-gold-500" />
                                <span>{[a.locality, a.city].filter(Boolean).join(", ") || a.city}</span>
                            </div>

                            <div className="mt-6 rounded-xl bg-white/5 border border-white/10 p-4">
                                <p className="text-sm text-white/80 leading-relaxed">
                                    <strong className="text-gold-400 font-semibold">Sign up or log in</strong> to view base price, current bid, photos and place your bid. It's free and takes less than 30 seconds.
                                </p>
                            </div>

                            <ul className="mt-6 space-y-2.5 text-sm text-white/75">
                                {[
                                    "View full auction details & current bid",
                                    "Place real-time bids on live auctions",
                                    "Get notified when an auction goes live",
                                    "Earn ₹200 signup bonus on registration",
                                ].map((b) => (
                                    <li key={b} className="flex items-start gap-2.5">
                                        <CheckCircle2 className="w-4 h-4 text-gold-500 mt-0.5 shrink-0" />
                                        <span>{b}</span>
                                    </li>
                                ))}
                            </ul>

                            <div className="mt-8 flex flex-col sm:flex-row gap-3">
                                <Link
                                    to={`/register?next=${next}`}
                                    data-testid="auction-loginwall-register"
                                    className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-gold-500 text-navy-900 font-bold text-sm tracking-wide hover:bg-gold-100 transition-colors shadow-lg shadow-gold-500/20"
                                >
                                    <UserPlus className="w-4 h-4" /> Sign up free
                                </Link>
                                <Link
                                    to={`/login?next=${next}`}
                                    data-testid="auction-loginwall-login"
                                    className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-white/10 border border-white/20 text-white font-semibold text-sm hover:bg-white/15 transition-colors"
                                >
                                    <LogIn className="w-4 h-4" /> Log in
                                </Link>
                            </div>

                            <p className="text-[11px] text-white/45 mt-5 text-center">
                                By continuing you agree to our Terms & Privacy Policy.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
